import React, { useState, useEffect } from 'react';
import styles from './BillingAddressModal.module.css';

const BillingAddressModal = ({ isOpen, onClose, onSave, editingAddress }) => {
  const [formData, setFormData] = useState({
    first_name: '',
    last_name: '',
    email: '',
    address_line1: '',
    address_line2: '',
    city: '',
    state: '',
    postal_code: '',
    country: '',
    is_default: false
  });
  const [errors, setErrors] = useState({});
  const [isSubmitting, setIsSubmitting] = useState(false);

  useEffect(() => {
    if (editingAddress) {
      setFormData(editingAddress);
    } else {
      setFormData({
        first_name: '',
        last_name: '',
        email: '',
        address_line1: '',
        address_line2: '',
        city: '',
        state: '',
        postal_code: '',
        country: '',
        is_default: false
      });
    }
    setErrors({});
  }, [editingAddress]);

  const handleSubmit = async (e) => {
    e.preventDefault();
    
    // Validate form
    const newErrors = {};
    if (!formData.first_name) newErrors.first_name = 'First name is required';
    if (!formData.last_name) newErrors.last_name = 'Last name is required';
    if (!formData.email) {
      newErrors.email = 'Email is required';
    } else if (!/\S+@\S+\.\S+/.test(formData.email)) {
      newErrors.email = 'Invalid email format';
    }
    if (!formData.address_line1) newErrors.address_line1 = 'Address is required';
    if (!formData.city) newErrors.city = 'City is required';
    if (!formData.state) newErrors.state = 'State is required';
    if (!formData.postal_code) newErrors.postal_code = 'Postal code is required';
    if (!formData.country) newErrors.country = 'Country is required';

    if (Object.keys(newErrors).length > 0) {
      setErrors(newErrors);
      return;
    }

    setIsSubmitting(true);
    try {
      await onSave(formData);
      onClose();
    } catch (error) {
      setErrors({ submit: error.message });
    } finally {
      setIsSubmitting(false);
    }
  };

  const handleChange = (field, value) => {
    setFormData(prev => ({
      ...prev,
      [field]: value
    }));
    // Clear error for the field being changed
    if (errors[field]) {
      setErrors(prev => ({
        ...prev,
        [field]: undefined
      }));
    }
  };

  if (!isOpen) return null;

  return (
    <div className={styles.modalOverlay} onClick={(e) => e.target === e.currentTarget && onClose()}>
      <div className={styles.modal}>
        <div className={styles.modalHeader}>
          <h2>{editingAddress ? 'Edit Billing Address' : 'Add Billing Address'}</h2>
          <button className={styles.closeButton} onClick={onClose}>
            <i className="fas fa-times"></i>
          </button>
        </div>
        
        <form onSubmit={handleSubmit} className={styles.form}>
          <div className={styles.formRow}>
            <div className={styles.formGroup}>
              <label>First Name</label>
              <input
                type="text"
                placeholder="John"
                value={formData.first_name}
                onChange={(e) => handleChange('first_name', e.target.value)}
                className={errors.first_name ? styles.error : ''}
              />
              {errors.first_name && <span className={styles.errorText}>{errors.first_name}</span>}
            </div>
            <div className={styles.formGroup}>
              <label>Last Name</label>
              <input
                type="text"
                placeholder="Doe"
                value={formData.last_name}
                onChange={(e) => handleChange('last_name', e.target.value)}
                className={errors.last_name ? styles.error : ''}
              />
              {errors.last_name && <span className={styles.errorText}>{errors.last_name}</span>}
            </div>
          </div>

          <div className={styles.formGroup}>
            <label>Email</label>
            <input
              type="email"
              placeholder="john.doe@example.com"
              value={formData.email}
              onChange={(e) => handleChange('email', e.target.value)}
              className={errors.email ? styles.error : ''}
            />
            {errors.email && <span className={styles.errorText}>{errors.email}</span>}
          </div>

          <div className={styles.formGroup}>
            <label>Address Line 1</label>
            <input
              type="text"
              placeholder="123 Main St"
              value={formData.address_line1}
              onChange={(e) => handleChange('address_line1', e.target.value)}
              className={errors.address_line1 ? styles.error : ''}
            />
            {errors.address_line1 && <span className={styles.errorText}>{errors.address_line1}</span>}
          </div>

          <div className={styles.formGroup}>
            <label>Address Line 2 (Optional)</label>
            <input
              type="text"
              placeholder="Apt 4B"
              value={formData.address_line2}
              onChange={(e) => handleChange('address_line2', e.target.value)}
            />
          </div>

          <div className={styles.formRow}>
            <div className={styles.formGroup}>
              <label>City</label>
              <input
                type="text"
                placeholder="New York"
                value={formData.city}
                onChange={(e) => handleChange('city', e.target.value)}
                className={errors.city ? styles.error : ''}
              />
              {errors.city && <span className={styles.errorText}>{errors.city}</span>}
            </div>
            <div className={styles.formGroup}>
              <label>State</label>
              <input
                type="text"
                placeholder="NY"
                value={formData.state}
                onChange={(e) => handleChange('state', e.target.value)}
                className={errors.state ? styles.error : ''}
              />
              {errors.state && <span className={styles.errorText}>{errors.state}</span>}
            </div>
          </div>

          <div className={styles.formRow}>
            <div className={styles.formGroup}>
              <label>Postal Code</label>
              <input
                type="text"
                placeholder="10001"
                value={formData.postal_code}
                onChange={(e) => handleChange('postal_code', e.target.value)}
                className={errors.postal_code ? styles.error : ''}
              />
              {errors.postal_code && <span className={styles.errorText}>{errors.postal_code}</span>}
            </div>
            <div className={styles.formGroup}>
              <label>Country</label>
              <input
                type="text"
                placeholder="United States"
                value={formData.country}
                onChange={(e) => handleChange('country', e.target.value)}
                className={errors.country ? styles.error : ''}
              />
              {errors.country && <span className={styles.errorText}>{errors.country}</span>}
            </div>
          </div>

          <div className={styles.formGroup}>
            <label className={styles.checkboxLabel} style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
              <input
                type="checkbox"
                checked={formData.is_default}
                onChange={(e) => handleChange('is_default', e.target.checked)}
                style={{ margin: 0 }}
              />
              <span>Set as default billing address</span>
            </label>
          </div>

          {errors.submit && (
            <div className={styles.submitError}>
              {errors.submit}
            </div>
          )}

          <div className={styles.formActions}>
            <button 
              type="button" 
              className={styles.cancelButton} 
              onClick={onClose}
              disabled={isSubmitting}
            >
              Cancel
            </button>
            <button type="submit" className={styles.saveButton} disabled={isSubmitting}>
              {isSubmitting ? (
                <><i className="fas fa-spinner fa-spin"></i> Saving...</>
              ) : (
                editingAddress ? 'Update Address' : 'Add Address'
              )}
            </button>
          </div>
        </form>
      </div>
    </div>
  );
};

export default BillingAddressModal; 